import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef, useMemo } from 'react';
import { Row, Col, Form, Card, Alert } from 'react-bootstrap';
import { FaThermometerHalf, FaTint, FaExclamationTriangle } from 'react-icons/fa';
import { patchCondicionesBulk } from '../../../../api/common';
import AutoguardadoIndicator from '../components/AutoguardadoIndicator';

const StepCondicionesAmbientales = forwardRef(({ loteEquipos, lotesCalibracion, onDataChange }, ref) => {
  const [condiciones, setCondiciones] = useState({
    temperaturaMax: '',
    temperaturaMin: '',
    humedadMax: '',
    humedadMin: ''
  });

  const [autoguardado, setAutoguardado] = useState('idle'); // idle | pending | saving | saved | error
  const timerRef = useRef(null);

  // Cargar datos existentes
  useEffect(() => {
    if (lotesCalibracion && lotesCalibracion.length > 0) {
      const primerLote = lotesCalibracion[0];
      const cond = primerLote.condicionesAmbientales || {};
      setCondiciones({
        temperaturaMax: cond.temperaturaMax || '',
        temperaturaMin: cond.temperaturaMin || '',
        humedadMax: cond.humedadMax || '',
        humedadMin: cond.humedadMin || ''
      });
    }
  }, [lotesCalibracion]);

  // Guardar cambios automáticamente después de 2 segundos
  const guardarCambios = useCallback(async (data) => {
    setAutoguardado('saving');
    try {
      await patchCondicionesBulk(loteEquipos._id, {
        condicionesAmbientales: data
      });
      setAutoguardado('saved');
      if (onDataChange) onDataChange();
      
      // Volver a idle después de 2 segundos
      setTimeout(() => setAutoguardado('idle'), 2000);
    } catch (error) {
      console.error('Error guardando condiciones:', error);
      setAutoguardado('error');
    }
  }, [loteEquipos, onDataChange]);

  const handleChange = (campo, valor) => {
    const nuevoValor = valor === '' ? '' : parseFloat(valor);
    const nuevasCondiciones = { ...condiciones, [campo]: nuevoValor };
    setCondiciones(nuevasCondiciones);

    // Limpiar timer anterior
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Marcar como pendiente
    setAutoguardado('pending');

    // Programar guardado automático
    timerRef.current = setTimeout(() => {
      guardarCambios(nuevasCondiciones);
    }, 2000);
  };

  useEffect(() => {
    // Limpiar timer al desmontar
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const reintentar = () => {
    guardarCambios(condiciones);
  };

  const guardarAhora = useCallback(() => {
    // Cancelar timer pendiente
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    // Guardar inmediatamente si hay cambios pendientes
    if (autoguardado === 'pending') {
      return guardarCambios(condiciones);
    }
    // Si no hay cambios pendientes, retornar promesa resuelta
    return Promise.resolve();
  }, [condiciones, autoguardado, guardarCambios]);

  // Validación cruzada Min≤Max para temperatura y humedad.
  // Si min o max quedan como '' se ignora la comparación (input aún incompleto).
  const validaciones = useMemo(() => {
    const errs = { temperatura: null, humedad: null };
    const tMin = condiciones.temperaturaMin;
    const tMax = condiciones.temperaturaMax;
    const hMin = condiciones.humedadMin;
    const hMax = condiciones.humedadMax;
    if (tMin !== '' && tMax !== '' && Number(tMin) > Number(tMax)) {
      errs.temperatura = 'La temperatura mínima no puede ser mayor que la máxima.';
    }
    if (hMin !== '' && hMax !== '' && Number(hMin) > Number(hMax)) {
      errs.humedad = 'La humedad mínima no puede ser mayor que la máxima.';
    }
    return errs;
  }, [condiciones]);

  const hayErrores = Boolean(validaciones.temperatura || validaciones.humedad);

  // Exponer el método guardarAhora al componente padre. `esValido` se usa
  // desde RegistroMediciones para bloquear "Finalizar Lote" si min>max.
  useImperativeHandle(ref, () => ({
    guardarAhora,
    tieneCambiosPendientes: () => autoguardado === 'pending',
    esValido: () => !hayErrores,
    obtenerErrores: () => validaciones
  }), [guardarAhora, autoguardado, hayErrores, validaciones]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">Condiciones Ambientales</h5>
        <AutoguardadoIndicator 
          estado={autoguardado} 
          onReintentar={reintentar} 
          onGuardarAhora={guardarAhora}
        />
      </div>

      <p className="text-muted mb-4">
        Estas condiciones aplican para todos los equipos del lote
      </p>

      {(validaciones.temperatura || validaciones.humedad) && (
        <Alert variant="danger" className="d-flex align-items-start">
          <FaExclamationTriangle className="me-2 mt-1" />
          <div>
            <strong>Rango inválido — no se puede finalizar el lote</strong>
            <ul className="mb-0 mt-1">
              {validaciones.temperatura && <li>{validaciones.temperatura}</li>}
              {validaciones.humedad && <li>{validaciones.humedad}</li>}
            </ul>
          </div>
        </Alert>
      )}

      <Row>
        <Col md={6}>
          <Card className="shadow-sm mb-4 w-100" style={{borderRadius: '12px'}}>
            <Card.Header className="bg-danger text-white w-100" style={{borderRadius: '12px 12px 0 0'}}>
              <FaThermometerHalf className="me-2" /> Temperatura (°C)
            </Card.Header>
            <Card.Body className="w-100">
              <Form.Group className="mb-3">
                <Form.Label>Máxima</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  onWheel={(e) => e.currentTarget.blur()}
                  value={condiciones.temperaturaMax}
                  onChange={(e) => handleChange('temperaturaMax', e.target.value)}
                  placeholder="Ej: 25.5"
                  isInvalid={Boolean(validaciones.temperatura)}
                  style={{
                    borderColor: validaciones.temperatura
                      ? '#dc3545'
                      : (condiciones.temperaturaMax !== '' ? '#28a745' : '#dee2e6')
                  }}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Mínima</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  onWheel={(e) => e.currentTarget.blur()}
                  value={condiciones.temperaturaMin}
                  onChange={(e) => handleChange('temperaturaMin', e.target.value)}
                  placeholder="Ej: 18.0"
                  isInvalid={Boolean(validaciones.temperatura)}
                  style={{
                    borderColor: validaciones.temperatura
                      ? '#dc3545'
                      : (condiciones.temperaturaMin !== '' ? '#28a745' : '#dee2e6')
                  }}
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="shadow-sm mb-4 w-100" style={{borderRadius: '12px'}}>
            <Card.Header className="bg-info text-white w-100" style={{borderRadius: '12px 12px 0 0'}}>
              <FaTint className="me-2" /> Humedad Relativa (%)
            </Card.Header>
            <Card.Body className="w-100">
              <Form.Group className="mb-3">
                <Form.Label>Máxima</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  onWheel={(e) => e.currentTarget.blur()}
                  value={condiciones.humedadMax}
                  onChange={(e) => handleChange('humedadMax', e.target.value)}
                  placeholder="Ej: 65.0"
                  isInvalid={Boolean(validaciones.humedad)}
                  style={{
                    borderColor: validaciones.humedad
                      ? '#dc3545'
                      : (condiciones.humedadMax !== '' ? '#28a745' : '#dee2e6')
                  }}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Mínima</Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  onWheel={(e) => e.currentTarget.blur()}
                  value={condiciones.humedadMin}
                  onChange={(e) => handleChange('humedadMin', e.target.value)}
                  placeholder="Ej: 45.0"
                  isInvalid={Boolean(validaciones.humedad)}
                  style={{
                    borderColor: validaciones.humedad
                      ? '#dc3545'
                      : (condiciones.humedadMin !== '' ? '#28a745' : '#dee2e6')
                  }}
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
});

export default StepCondicionesAmbientales;
